import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./KeyholderPaywall.module.scss"

/**
 * KeyholderPaywall — an original, self-contained NON-ONBOARDING paywall.
 *
 * Concept ("Become a Keyholder"): instead of a feature grid or a fear-based
 * countdown, the upgrade is reframed as a by-invitation membership. The hero is
 * a single, tactile, holographic black-metal MEMBERSHIP CARD that lives in 3D —
 * it tilts toward the pointer (and the device gyroscope on a phone), and a gold
 * holographic foil sweeps across the surface as it moves. The purchase isn't
 * "subscribe", it's "claim your card" and become one of the keyholders.
 *
 * The whole layout funnels to the YEARLY plan: the annual "Keyholder" card is the
 * only large, glowing, tappable surface, its annual price is the single biggest
 * number on screen (with a tiny per-month equivalent so the commitment feels small
 * and it stays App Store-compliant — the annual price is the most prominent price
 * for the annual product), the savings vs monthly is shouted, and monthly is
 * demoted to one quiet line of text.
 *
 * Fully self-contained: no shared paywall components are reused. It reads real
 * pricing from the app's debug product data and talks to the native bridge
 * directly, so it renders standalone with the built-in debug data. Reuses the
 * brand fonts (RedHatDisplay + KronaOne for the wordmark), the brand primary blue
 * and secondary gold, and existing repo feature icons for the perk list.
 */

const DUST_COUNT = 22

const KeyholderPaywall = () => {
  const { state, actions } = useAppState()
  const productData = state.productData
  const reduceMotion = useReducedMotion()

  const [showClose, setShowClose] = useState(false)
  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setHeaderInsetColor("#07090f")
    actions.setFooterInsetColor("#07090f")
    const t = setTimeout(() => setShowClose(true), 2600)
    return () => clearTimeout(t)
  }, [actions])

  const premium = productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const perMonthEquivalent = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 78
    const ratio = yearlyProduct.price / (monthlyProduct.price * 12)
    return Math.round((1 - ratio) * 100)
  }, [yearlyProduct, monthlyProduct])

  const guardedCount =
    productData?.numPhotos && productData.numPhotos > 0
      ? productData.numPhotos
      : 2174

  const topInset = state.initialData?.originalTopInset ?? 0
  const bottomInset = state.initialData?.originalBottomInset ?? 0

  const handleClose = () => native.postNativeEvent("close")

  const handlePurchase = () => {
    const product: Product | undefined =
      selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  return (
    <div className={styles.screen}>
      <Ambiance reduceMotion={!!reduceMotion} />

      <AnimatePresence>
        {showClose && (
          <motion.button
            className={styles.close}
            style={{ top: topInset + 14 }}
            onClick={handleClose}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 0.55, scale: 1 }}
            whileTap={{ scale: 0.85, opacity: 1 }}
            aria-label="Close"
          >
            ✕
          </motion.button>
        )}
      </AnimatePresence>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 18, paddingBottom: bottomInset + 16 }}
      >
        <motion.div
          className={styles.heading}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            BY INVITATION · MEMBERS ONLY
          </span>
          <h1 className={styles.title}>
            Become a<br />
            <span className={styles.titleAccent}>Keyholder.</span>
          </h1>
          <p className={styles.subtitle}>
            The highest tier of your private vault — and the only key that opens
            everything. Issued once, yours for the year.
          </p>
        </motion.div>

        <MembershipCard
          reduceMotion={!!reduceMotion}
          memberNo="04"
          guardedCount={guardedCount}
        />

        <PerkList />

        <motion.div
          className={styles.plans}
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.62, duration: 0.6 }}
        >
          <button
            className={`${styles.yearCard} ${
              selected === "yearly" ? styles.yearCardActive : ""
            }`}
            onClick={() => setSelected("yearly")}
            aria-pressed={selected === "yearly"}
          >
            <span className={styles.foilEdge} aria-hidden />
            <span className={styles.bestRibbon}>SAVE {savings}%</span>

            <div className={styles.yearHead}>
              <span
                className={`${styles.radio} ${
                  selected === "yearly" ? styles.radioOn : ""
                }`}
                aria-hidden
              >
                {selected === "yearly" && <Check />}
              </span>
              <div className={styles.yearHeadText}>
                <span className={styles.yearName}>Keyholder · Annual</span>
                <span className={styles.yearSub}>
                  Your card, issued for a full year
                </span>
              </div>
            </div>

            <div className={styles.priceBlock}>
              <div className={styles.priceRow}>
                <span className={styles.priceMain}>{yearlyPrice}</span>
                <span className={styles.pricePer}>/year</span>
              </div>
              <div className={styles.priceSub}>
                just {perMonthEquivalent}/mo · billed annually
              </div>
            </div>
          </button>

          <button
            className={`${styles.monthlyLine} ${
              selected === "monthly" ? styles.monthlyLineOn : ""
            }`}
            onClick={() => setSelected("monthly")}
            aria-pressed={selected === "monthly"}
          >
            Prefer monthly? {monthlyPrice}/mo — standard rate
          </button>
        </motion.div>

        <motion.div
          className={styles.ctaWrap}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.76, duration: 0.6 }}
        >
          <button className={styles.cta} onClick={handlePurchase}>
            <span className={styles.ctaShimmer} aria-hidden />
            <KeyGlyph className={styles.ctaKey} />
            <span className={styles.ctaLabel}>
              {selected === "yearly" ? "Claim my card" : "Continue monthly"}
            </span>
            <span className={styles.ctaArrow}>→</span>
          </button>
          <p className={styles.ctaNote}>
            Unlocks instantly · Cancel anytime in one tap
          </p>
        </motion.div>

        <TrustRow />

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Backdrop: a dark vault-room ambiance — a slow moving spotlight, a    */
/* faint concentric vault dial, drifting gold dust and a vignette.      */
/* ------------------------------------------------------------------ */
const Ambiance = ({ reduceMotion }: { reduceMotion: boolean }) => {
  const dust = useMemo(
    () =>
      Array.from({ length: DUST_COUNT }, (_, i) => {
        const r1 = Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1
        const r2 = Math.abs(Math.sin(i * 78.233) * 12543.123) % 1
        const r3 = Math.abs(Math.sin(i * 4.123) * 9973.77) % 1
        return {
          left: `${r1 * 100}%`,
          size: 1.5 + r3 * 3,
          delay: `${r2 * 10}s`,
          dur: `${9 + r1 * 9}s`,
          drift: `${(r3 - 0.5) * 50}px`,
        }
      }),
    [],
  )

  return (
    <div className={styles.scene} aria-hidden>
      <div className={styles.spotlight} />
      <div className={styles.dial} />
      <div className={styles.dialInner} />
      {!reduceMotion && (
        <div className={styles.dust}>
          {dust.map((d, i) => (
            <span
              key={i}
              className={styles.mote}
              style={
                {
                  left: d.left,
                  width: d.size,
                  height: d.size,
                  animationDelay: d.delay,
                  animationDuration: d.dur,
                  "--drift": d.drift,
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      )}
      <div className={styles.vignette} />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* The hero: a holographic black-metal membership card that tilts in    */
/* 3D toward the pointer / device gyro, with a sweeping gold foil.       */
/* ------------------------------------------------------------------ */
const MembershipCard = ({
  reduceMotion,
  memberNo,
  guardedCount,
}: {
  reduceMotion: boolean
  memberNo: string
  guardedCount: number
}) => {
  const stageRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  const apply = useCallback(
    (rx: number, ry: number, px: number, py: number) => {
      const el = cardRef.current
      if (!el) return
      el.style.setProperty("--rx", `${rx.toFixed(2)}deg`)
      el.style.setProperty("--ry", `${ry.toFixed(2)}deg`)
      el.style.setProperty("--mx", `${px.toFixed(1)}%`)
      el.style.setProperty("--my", `${py.toFixed(1)}%`)
    },
    [],
  )

  const reset = useCallback(() => apply(0, 0, 50, 50), [apply])

  useEffect(() => {
    if (reduceMotion) return
    const stage = stageRef.current
    if (!stage) return

    const onMove = (clientX: number, clientY: number) => {
      const r = stage.getBoundingClientRect()
      const x = (clientX - r.left) / r.width // 0..1
      const y = (clientY - r.top) / r.height
      const ry = (x - 0.5) * 26 // rotateY
      const rx = (0.5 - y) * 20 // rotateX
      apply(rx, ry, x * 100, y * 100)
    }

    const onPointer = (e: PointerEvent) => onMove(e.clientX, e.clientY)
    const onLeave = () => reset()

    stage.addEventListener("pointermove", onPointer)
    stage.addEventListener("pointerleave", onLeave)

    // Device gyroscope on a real phone — gentle, clamped.
    const onTilt = (e: DeviceOrientationEvent) => {
      const gamma = Math.max(-30, Math.min(30, e.gamma ?? 0)) // L/R
      const beta = Math.max(-30, Math.min(30, (e.beta ?? 0) - 35)) // F/B
      apply(-beta * 0.5, gamma * 0.7, 50 + gamma, 50 + beta)
    }
    window.addEventListener("deviceorientation", onTilt)

    return () => {
      stage.removeEventListener("pointermove", onPointer)
      stage.removeEventListener("pointerleave", onLeave)
      window.removeEventListener("deviceorientation", onTilt)
    }
  }, [apply, reset, reduceMotion])

  return (
    <motion.div
      className={styles.stage}
      ref={stageRef}
      initial={{ opacity: 0, y: 26, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.3, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={styles.cardGlow} aria-hidden />
      <div
        className={`${styles.card} ${reduceMotion ? styles.cardStill : ""}`}
        ref={cardRef}
      >
        <div className={styles.cardMetal} aria-hidden />
        <div className={styles.cardFoil} aria-hidden />
        <div className={styles.cardSheen} aria-hidden />
        <div className={styles.cardGrain} aria-hidden />

        <div className={styles.cardTop}>
          <span className={styles.wordmark}>KEYHOLDER</span>
          <span className={styles.tier}>ANNUAL</span>
        </div>

        <div className={styles.chip} aria-hidden>
          <div className={styles.chipPlate} />
          <KeyGlyph className={styles.chipKey} />
        </div>

        <div className={styles.cardNo}>NO. {memberNo} · 0041 · 2026</div>

        <div className={styles.cardBottom}>
          <div className={styles.cardHolder}>
            <span className={styles.cardHolderLabel}>MEMBER SINCE</span>
            <span className={styles.cardHolderValue}>2026</span>
          </div>
          <div className={styles.cardHolder}>
            <span className={styles.cardHolderLabel}>GUARDING</span>
            <span className={styles.cardHolderValue}>
              {guardedCount.toLocaleString()}
            </span>
          </div>
          <div className={styles.hologram} />
        </div>
      </div>
      <span className={styles.tiltHint}>
        Tilt to see the foil catch the light
      </span>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ */
/* What the key opens — a refined perk list using repo feature icons.   */
/* ------------------------------------------------------------------ */
const PERKS = [
  {
    Icon: svg.featureIcons.CloudVault,
    text: "Unlimited encrypted cloud vault",
  },
  {
    Icon: svg.featureIcons.Breakin,
    text: "Break-in alerts & intruder capture",
  },
  { Icon: svg.featureIcons.Decoy, text: "Decoy vault & app disguise" },
  { Icon: svg.featureIcons.Star, text: "Ad-free, members-only experience" },
]

const PerkList = () => {
  return (
    <motion.div
      className={styles.perks}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5, duration: 0.6 }}
    >
      <span className={styles.perksTitle}>Your key opens</span>
      <div className={styles.perkRows}>
        {PERKS.map((p, i) => (
          <motion.div
            key={i}
            className={styles.perk}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.56 + i * 0.08, duration: 0.45 }}
          >
            <span className={styles.perkIcon}>
              <p.Icon />
            </span>
            <span className={styles.perkText}>{p.text}</span>
            <span className={styles.perkCheck}>
              <Check />
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
}

const TrustRow = () => {
  return (
    <motion.div
      className={styles.trust}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.9, duration: 0.6 }}
    >
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>4.8★</span>
        <span className={styles.trustLabel}>App Store</span>
      </div>
      <span className={styles.trustSep} />
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>10M+</span>
        <span className={styles.trustLabel}>members</span>
      </div>
      <span className={styles.trustSep} />
      <div className={styles.trustItem}>
        <span className={styles.trustStrong}>Zero-knowledge</span>
        <span className={styles.trustLabel}>only you hold the key</span>
      </div>
    </motion.div>
  )
}

/* ---- inline glyphs ---- */
const KeyGlyph = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="8" cy="8" r="4.4" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" />
    <path
      d="M11 11l8.2 8.2M16.4 16.4l2-2M18.6 18.6l2-2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden width="100%" height="100%">
    <path
      d="M5 12.5l4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default KeyholderPaywall
